import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import AsyncSelect from 'react-select/async';
import { useTranslate } from '../../../components/TranslationText';
import EahsForm from '../../../components/EahsForm';
import { formatDate } from '../../../helper/formatDate';
import {
    FormTextInput,
    FormDateInput,
    FormFileUpload,
    FormSelect,
    FormYesNo,
    FormCheckbox,
} from '../../../components/FormInputs';
import { eformInput } from '../../../common/cssclasses';
import { promiseOptions } from '../Api/semenMarketApi';
import { formatDateTimeToString } from '../../../helper/formatDateTimeToString';

/**
 * Mare Details section of the SemenCheckout form.
 * Reads form state from FormProvider context.
 *
 * @param {Array} colors - Color options for select
 * @param {Array} countries - Country options for select (origin)
 * @param {boolean} disabled - Disable all inputs (view-only)
 * @param {object|null} selectedMare - Currently selected registered mare
 * @param {function} onSelectedMareChange - Callback when mare selection changes
 */
export default function SemenMareDetails({
    colors,
    countries,
    disabled = false,
    selectedMare,
    onSelectedMareChange,
    profile,
}) {
    const { translate } = useTranslate();
    const { watch, setValue, getValues, formState: { errors } } = useFormContext();
    const isRegisteredMare = watch('isRegisteredMare');
    const isMareOwner = watch('isMareOwner');
    const mareIdError = errors?.mareId;

    const [searchMare, setSearchMare] = useState('');
    const [timeoutId, setTimeoutId] = useState(0);

    const handleMareChange = (option) => {
        onSelectedMareChange(option);
        setValue('mareId', option?.value || '', { shouldValidate: true });
    };

    React.useEffect(() => {
        if (isRegisteredMare) {
            // Switched to Registered -> clear manual fields
            setValue('mareNameEn', '');
            setValue('mareNameAr', '');
            setValue('sireNameEn', '');
            setValue('sireNameAr', '');
            setValue('damNameEn', '');
            setValue('damNameAr', '');
            setValue('mareBirthDate', '');
            setValue('colorId', null);
            setValue('originId', null);
            setValue('marePassport', null);
        } else {
            // Switched to Unregistered -> clear selected mare dropdown ID
            setValue('mareId', '');
            onSelectedMareChange(null);
        }
    }, [isRegisteredMare, setValue, onSelectedMareChange]);

    // When isMareOwner changes: checked = fill from profile (owned by me)
    React.useEffect(() => {
        if (isMareOwner) {
            const userProfile = JSON.parse(localStorage.getItem('eahsUserProfile'));
            const label = profile?.label || localStorage.getItem('EAHSUserFullName') || '';
            // Only fill if fields are empty to avoid overwriting existing data
            if (!getValues('ownerNameEn')) setValue('ownerNameEn', userProfile?.ownerNameEn || label);
            if (!getValues('ownerNameAr')) setValue('ownerNameAr', userProfile?.ownerNameAr || label);
        }
    }, [isMareOwner, profile, setValue, getValues]);

    const onChangeOwnedByMe = (e) => {
        const checked = e.target.checked;
        setValue('isMareOwner', checked);
        if (checked) {
            const userProfile = JSON.parse(localStorage.getItem('eahsUserProfile'));
            const label = profile?.label || localStorage.getItem('EAHSUserFullName') || '';
            setValue('ownerNameEn', userProfile?.ownerNameEn || label);
            setValue('ownerNameAr', userProfile?.ownerNameAr || label);
        } else {
            setValue('ownerNameEn', '');
            setValue('ownerNameAr', '');
        }
    };

    return (
        <EahsForm key="mareDetails" title={translate('web.mareDetails', 'Mare Details')}>
            <div className={eformInput}>
                <FormYesNo
                    name="isRegisteredMare"
                    label={translate('web.isMareRegistered', 'Is Mare Registered')}
                    required
                    noValidate={true}
                    disabled={disabled}
                />
            </div>

            {/* Registered mare — async search select */}
            {isRegisteredMare && (
                <>
                <div className={eformInput}>
                    <label>
                        {translate('web.mare', 'Mare')}
                        <i style={{ color: 'red', margin: 'auto 2px' }}>*</i>
                    </label>
                    <AsyncSelect
                        isClearable
                        loadOptions={(key) =>
                            promiseOptions(key, 'Mare', timeoutId, setTimeoutId)
                        }
                        defaultOptions={false}
                        onInputChange={(v) => setSearchMare(v)}
                        inputValue={searchMare}
                        value={selectedMare}
                        loadingMessage={() => translate('web.loading', 'Loading')}
                        onChange={handleMareChange}
                        placeholder={translate('web.typeToSearch', 'Type To Search') + '...'}
                        noOptionsMessage={() => translate('web.noOptions', 'No Options')}
                        isDisabled={disabled}
                        styles={{
                            control: (base) => ({
                                ...base,
                                ...(mareIdError ? { borderColor: 'red' } : {}),
                            }),
                        }}
                    />
                    {mareIdError && (
                        <div className="text-danger" style={{ fontSize: 12, marginTop: 4 }}>
                            {mareIdError.message}
                        </div>
                    )}
                </div>
                
                {selectedMare?.details && (
                    <div className="row mt-3 px-3">
                        <div className={eformInput}>
                            <FormTextInput
                                label={translate('web.origin', 'Origin')}
                                name="temp_origin"
                                value={selectedMare.details.originName || selectedMare.details.origin || ''}
                                disabled
                            />
                        </div>
                        <div className={eformInput}>
                            <FormTextInput
                                label={translate('web.birthDate', 'BirthDate')}
                                name="temp_birthDate"
                                value={selectedMare.details.birthDate ? formatDateTimeToString(selectedMare.details.birthDateObject) : ''}
                                disabled
                            />
                        </div>
                        <div className={eformInput}>
                            <FormTextInput
                                label={translate('web.sex', 'Sex')}
                                name="temp_sex"
                                value={translate("web." + selectedMare.details.gender || '')}
                                disabled
                            />
                        </div>
                        <div className={eformInput}>
                            <FormTextInput
                                label={translate('web.color', 'Color')}
                                name="temp_color"
                                value={selectedMare.details.colorName || selectedMare.details.color || ''}
                                disabled
                            />
                        </div>
                        <div className={eformInput}>
                            <FormTextInput
                                label={translate('web.sire', 'Sire')}
                                name="temp_sire"
                                value={selectedMare.details.sireName || selectedMare.details.sire || ''}
                                disabled
                            />
                        </div>
                        <div className={eformInput}>
                            <FormTextInput
                                label={translate('web.dam', 'Dam')}
                                name="temp_dam"
                                value={selectedMare.details.damName || selectedMare.details.dam || ''}
                                disabled
                            />
                        </div>
                        <div className={eformInput}>
                            <FormTextInput
                                label={translate('web.breeder', 'Breeder')}
                                name="temp_breeder"
                                value={selectedMare.details.breederName || selectedMare.details.breeder || ''}
                                disabled
                            />
                        </div>
                        <div className={eformInput}>
                            <FormTextInput
                                label={translate('web.ownerName', 'Owner Name')}
                                name="temp_ownerName"
                                value={selectedMare.details.ownerName || selectedMare.details.owner || ''}
                                disabled
                            />
                        </div>
                        <div className={eformInput}>
                            <FormTextInput
                                label={translate('web.email', 'Email')}
                                name="temp_email"
                                value={selectedMare.details.ownerEmail || ''}
                                disabled
                            />
                        </div>
                        <div className={eformInput}>
                            <FormTextInput
                                label={translate('web.mobileNo', 'Mobile No')}
                                name="temp_mobileNo"
                                value={selectedMare.details.ownerMobile || ''}
                                disabled
                            />
                        </div>
                    </div>
                )}
                </>
                
            )}

            {/* Unregistered mare — manual fields */}
            {!isRegisteredMare && (
                <>
                    <div className={eformInput}>
                        <FormTextInput
                            name="mareNameEn"
                            label={translate('web.mare', 'Mare') + ' (EN)'}
                            required
                            noValidate={true}
                            disabled={disabled}
                        />
                    </div>
                    <div className={eformInput}>
                        <FormTextInput
                            name="mareNameAr"
                            label={translate('web.mare', 'Mare') + ' (AR)'}
                            required
                            noValidate={true}
                            disabled={disabled}
                        />
                    </div>
                    <div className={eformInput}>
                        <FormTextInput
                            name="sireNameEn"
                            label={translate('web.sire', 'Sire') + ' (EN)'}
                            required
                            noValidate={true}
                            disabled={disabled}
                        />
                    </div>
                    <div className={eformInput}>
                        <FormTextInput
                            name="sireNameAr"
                            label={translate('web.sire', 'Sire') + ' (AR)'}
                            required
                            noValidate={true}
                            disabled={disabled}
                        />
                    </div>
                    <div className={eformInput}>
                        <FormTextInput
                            name="damNameEn"
                            label={translate('web.dam', 'Dam') + ' (EN)'}
                            required
                            noValidate={true}
                            disabled={disabled}
                        />
                    </div>
                    <div className={eformInput}>
                        <FormTextInput
                            name="damNameAr"
                            label={translate('web.dam', 'Dam') + ' (AR)'}
                            required
                            noValidate={true}
                            disabled={disabled}
                        />
                    </div>
                    <div className={eformInput}>
                        <FormDateInput
                            name="mareBirthDate"
                            label={translate('web.birthDate', 'BirthDate')}
                            required
                            noValidate={true}
                            disabled={disabled}
                            minDate="1900-01-01"
                            maxDate={new Date().toISOString().split('T')[0]}
                        />
                    </div>
                    <div className={eformInput}>
                        <FormSelect
                            name="colorId"
                            label={translate('web.color', 'Color')}
                            options={colors}
                            required
                            noValidate={true}
                            disabled={disabled}
                        />
                    </div>
                    <div className={eformInput}>
                        <FormSelect
                            name="originId"
                            label={translate('web.origin', 'Origin')}
                            options={countries}
                            required
                            noValidate={true}
                            disabled={disabled}
                        />
                    </div>
                    <div className={eformInput}>
                        <FormTextInput
                            name="mareRegisterNumber"
                            label={translate('web.regNoOrPassportNo', 'Register No/Passport No')}
                            required
                            noValidate={true}
                            disabled={disabled}
                        />
                    </div>
                    <div className={eformInput}>
                        <FormFileUpload
                            name="marePassport"
                            label={translate('web.horsePassport', 'Horse Passport')}
                            required
                            noValidate={true}
                            disabled={disabled}
                        />
                    </div>

                    {/* ── Mare Owner Section ─────────────────────── */}
                    <div className="col-12 mb-form-group">
                        <FormCheckbox
                            name="isMareOwner"
                            label={translate('web.semenownedByMe', 'Owned By Me')}
                            disabled={disabled}
                            onChange={onChangeOwnedByMe}
                        />
                    </div>
                    <div className={eformInput}>
                        <FormTextInput
                            name="ownerNameEn"
                            label={translate('web.ownerNameEn', 'Owner Name (EN)')}
                            required={!isMareOwner}
                            noValidate={true}
                            disabled={disabled || isMareOwner}
                        />
                    </div>
                    <div className={eformInput}>
                        <FormTextInput
                            name="ownerNameAr"
                            label={translate('web.ownerNameAr', 'Owner Name (AR)')}
                            required={!isMareOwner}
                            noValidate={true}
                            disabled={disabled || isMareOwner}
                        />
                    </div>
                </>
            )}
        </EahsForm>
    );
}
